import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { MisSesiones, VistaSesion } from '../../../compartido/api';
import { fechaLarga, soles, tramos } from '../../../compartido/formato';
import { Camara, Check, Escudo, Mas, Reloj } from '../componentes/Iconos';
import { AvisoError, CabeceraMarca, Cambios, Cargando } from '../componentes/Piezas';
import { api, ErrorDeApi, mensajeDeError } from '../lib/api';
import { cuantoFalta, instante, sonSeguidas } from '../lib/fechas';
import { olvidarToken, tomarTokenDeLaUrl } from '../lib/sesion';

const MAX_CAMBIOS = 2;

/** Fin real de una sesión: el final de su última hora. */
export const finDe = (s: Pick<VistaSesion, 'fecha' | 'horas'>) => instante(s.fecha, Math.max(...s.horas) + 1);

/** Las sesiones de quien entró con el enlace del correo. */
export function MiSesion() {
  const navegar = useNavigate();
  const { state } = useLocation() as { state?: { aviso?: string } };
  const [token] = useState(() => tomarTokenDeLaUrl());
  const [datos, setDatos] = useState<MisSesiones>();
  const [error, setError] = useState<{ texto: string; vencido: boolean }>();

  useEffect(() => {
    if (!token) {
      navegar('/acceso', { replace: true });
      return;
    }
    api.misSesiones(token).then(setDatos, (e) => {
      const vencido = e instanceof ErrorDeApi && e.estado === 401;
      if (vencido) olvidarToken();
      setError({ texto: mensajeDeError(e), vencido });
    });
  }, [token, navegar]);

  if (error) {
    return (
      <div className="pantalla">
        <CabeceraMarca accion={{ a: '/', texto: 'Reservar' }} />
        <div className="contenido">
          <AvisoError>{error.texto}</AvisoError>
          {error.vencido && (
            <Link to="/acceso" className="boton">
              Pedir un enlace nuevo
            </Link>
          )}
        </div>
      </div>
    );
  }
  if (!datos) return <Cargando texto="Buscando tus sesiones…" />;

  const ahora = Date.now();
  const enPago = datos.sesiones.filter((s) => s.estado === 'EN_COBRO' || s.estado === 'PENDIENTE_PAGO');
  const confirmadas = datos.sesiones
    .filter((s) => s.estado === 'CONFIRMADA')
    .sort((x, y) => x.inicioEn.localeCompare(y.inicioEn));
  const proximas = confirmadas.filter((s) => finDe(s).getTime() > ahora);
  const anteriores = confirmadas.filter((s) => finDe(s).getTime() <= ahora).reverse();

  return (
    <div className="pantalla">
      <CabeceraMarca accion={{ a: '/', texto: 'Reservar' }} />
      <div className="contenido">
        {state?.aviso ? (
          <div className="aviso aviso--calido" role="status">
            <Check tam={16} color="#7A5A1E" />
            <span>{state.aviso}</span>
          </div>
        ) : (
          <div className="aviso">
            <Escudo />
            <span>
              Entraste como <span className="fuerte">{datos.correo}</span> con el enlace de tu correo. Sin cuenta ni contraseña.
            </span>
          </div>
        )}

        {enPago.map((s) => (
          <div key={s.id} className="aviso aviso--calido">
            <Reloj color="#7A5A1E" />
            <span>
              {s.estado === 'EN_COBRO' ? 'Estamos confirmando tu pago' : 'Tienes un pago sin terminar'} para el {fechaLarga(s.fecha)}.{' '}
              {s.estado === 'PENDIENTE_PAGO' && (
                <Link to={`/pago/${s.id}`} style={{ textDecoration: 'underline' }}>
                  Terminar el pago
                </Link>
              )}
            </span>
          </div>
        ))}

        <section>
          <h2 className="antetitulo">{proximas.length === 1 ? 'Próxima sesión' : 'Próximas sesiones'}</h2>
          {proximas.length === 0 ? (
            <p className="parrafo" style={{ marginTop: 10 }}>
              No tienes sesiones por venir.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 10 }}>
              {proximas.map((s) => (
                <TarjetaSesion key={s.id} s={s} />
              ))}
            </div>
          )}
        </section>

        {anteriores.length > 0 && (
          <section>
            <h2 className="antetitulo" style={{ color: 'var(--texto-3)' }}>
              Anteriores
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
              {anteriores.map((s) => (
                <div key={s.id} className="tarjeta" style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div className="fuerte" style={{ fontSize: 14 }}>
                      {capital(fechaLarga(s.fecha))}
                    </div>
                    <div className="nota">
                      {tramos(s.horas)} · {s.horas.length} {s.horas.length === 1 ? 'hora' : 'horas'}
                    </div>
                  </div>
                  <span className="fuerte" style={{ color: 'var(--texto-2)', fontSize: 13 }}>
                    {soles(s.precio)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      <div className="pie">
        <Link to="/" className="boton boton--secundario">
          <Mas />
          Reservar otro día
        </Link>
      </div>
    </div>
  );
}

function TarjetaSesion({ s }: { s: VistaSesion }) {
  const n = s.horas.length;
  const [dia, , ...resto] = fechaLarga(s.fecha).split(' ');
  return (
    <article className="tarjeta" style={{ padding: 17, gap: 13 }}>
      <div className="fila" style={{ alignItems: 'flex-start' }}>
        <div>
          <h3 className="monto" style={{ margin: 0, fontSize: 23 }}>
            {capital(dia!)} {s.fecha.slice(8).replace(/^0/, '')}
          </h3>
          <div className="parrafo" style={{ marginTop: 3 }}>
            {capital(resto.slice(1).join(' '))} · {tramos(s.horas)}
          </div>
        </div>
        <span className="chip" style={{ borderRadius: 999, flexShrink: 0 }}>
          {cuantoFalta(s.inicioEn)}
        </span>
      </div>

      <div className="chips">
        <span className="chip chip--gris">
          {n} {n === 1 ? 'hora' : 'horas'}
        </span>
        {n > 1 && <span className="chip chip--gris">{sonSeguidas(s.horas) ? 'Seguidas' : 'Con huecos'}</span>}
        <span className="chip chip--gris">{soles(s.precio)}</span>
      </div>

      {s.reunionUrl ? (
        <a href={s.reunionUrl} target="_blank" rel="noopener noreferrer" className="boton" style={{ minHeight: 48 }}>
          <Camara />
          Entrar a la reunión
        </a>
      ) : (
        <p className="nota">El enlace de la reunión aparecerá aquí antes de tu sesión.</p>
      )}

      {s.tema && (
        <p className="nota">
          <span className="fuerte">Lo que traes:</span> {s.tema}
        </p>
      )}

      <hr className="separador" />

      <div className="fila" style={{ alignItems: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span className="fuerte" style={{ fontSize: 13 }}>
            Cambios disponibles
          </span>
          <Cambios restantes={s.reprogramacionesRestantes} total={MAX_CAMBIOS} />
        </div>
        <Link to={`/reprogramar/${s.id}`} className="boton boton--secundario boton--chico">
          Reprogramar
        </Link>
      </div>
    </article>
  );
}

const capital = (x: string) => (x ? x[0]!.toUpperCase() + x.slice(1) : x);
